package com.banyan.flightviewer.web.components.map

import com.banyan.flightviewer.web.platform.L
import com.banyan.flightviewer.web.platform.LeafletMap
import com.banyan.flightviewer.web.platform.TileLayer
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.w3c.dom.Element
import kotlin.js.json

/**
 * Toggleable RainViewer precipitation radar overlay. Click [toggleBtn] to
 * enable -- fetches the latest frame index and drops a tile layer on the
 * map. Every 5 minutes it pulls the latest frame and swaps the tile layer
 * atomically (removes the old, adds the new) so tiles don't accumulate.
 *
 * Click again to disable: removes the tile layer and cancels the refresh
 * interval. Cleanup of both pieces of state is essential -- otherwise a
 * leftover interval keeps firing and fetches stack up over the lifetime of
 * the session.
 *
 * Mirrors [map.html toggleWeather()](desktop/src/main/resources/leaflet/map.html#L144).
 */
fun attachWeatherRadar(map: LeafletMap, toggleBtn: Element, scope: CoroutineScope) {
    var weatherLayer: TileLayer? = null
    var weatherTimestamp: String? = null
    var intervalId: Int? = null

    fun clearLayer() {
        weatherLayer?.let { map.removeLayer(it) }
        weatherLayer = null
        weatherTimestamp = null
    }

    fun refresh() {
        scope.launch {
            val framePath = fetchLatestRadarPath() ?: return@launch
            if (framePath == weatherTimestamp && weatherLayer != null) return@launch
            // Remove the old layer before adding the new one to keep exactly
            // one weather tile pane present at any moment.
            weatherLayer?.let { map.removeLayer(it) }
            weatherTimestamp = framePath
            weatherLayer = L.tileLayer(
                "https://tilecache.rainviewer.com$framePath/256/{z}/{x}/{y}/2/1_1.png",
                json("opacity" to 0.5, "maxZoom" to 18),
            ).addTo(map)
        }
    }

    toggleBtn.addEventListener("click", { _: org.w3c.dom.events.Event ->
        if (weatherLayer != null || intervalId != null) {
            // Toggle OFF: drop the layer and cancel the refresh interval.
            clearLayer()
            intervalId?.let { window.clearInterval(it) }
            intervalId = null
            toggleBtn.asDynamic().classList.remove("active")
        } else {
            toggleBtn.asDynamic().classList.add("active")
            refresh()
            intervalId = window.setInterval({ refresh() }, 300_000)
        }
    })
}

/**
 * Fetch the current RainViewer frame index and return the path of the
 * latest past frame (e.g. `/v2/radar/1704067200`). Returns null if the
 * response is malformed or the request fails -- caller treats null as
 * "keep current layer as-is".
 */
private suspend fun fetchLatestRadarPath(): String? {
    return try {
        val response = window.fetch("https://api.rainviewer.com/public/weather-maps.json").await()
        if (!response.ok) return null
        val body: dynamic = response.json().await()
        val past = body.radar?.past
        if (past == null || past.length == 0) return null
        val latest = past[past.length - 1]
        latest.path as? String
    } catch (_: Throwable) {
        null
    }
}
